import type { RequestPriority } from '@feedback/shared';
import { strings } from '../strings';

export interface PrioritySelectorProps {
  value: RequestPriority;
  onChange: (priority: RequestPriority) => void;
  /** Disables every option button — set by `Modal` while a submit is in flight. */
  disabled?: boolean;
}

/** Render order for the segmented control — matches `RequestPriorityEnum`'s member order. */
const PRIORITIES: RequestPriority[] = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];

const PRIORITY_LABEL_ID = 'ft-priority-label';

/**
 * Segmented control for picking a `RequestPriority`, mirroring {@link TypeSelector}:
 * the WAI-ARIA "radio group" pattern built from plain buttons (`role="radiogroup"` +
 * one `role="radio"` per option, `aria-checked` marking the selection), under a
 * visible label that is also the group's accessible name. Defaults to `NORMAL`,
 * set by `Modal`'s form state, and `Modal` offers it only for a Problem.
 */
export function PrioritySelector({ value, onChange, disabled }: PrioritySelectorProps) {
  return (
    <div class="ft-choice">
      <span id={PRIORITY_LABEL_ID} class="ft-field-label">
        {strings.priority.groupLabel}
      </span>
      <div class="ft-type-selector" role="radiogroup" aria-labelledby={PRIORITY_LABEL_ID}>
        {PRIORITIES.map((priority) => {
          const selected = priority === value;
          return (
            <button
              key={priority}
              type="button"
              role="radio"
              aria-checked={selected}
              class={selected ? 'ft-type-option ft-type-option--selected' : 'ft-type-option'}
              onClick={() => onChange(priority)}
              disabled={disabled}
            >
              {strings.priority.options[priority]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
